import { Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { WandSparkles } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { UuidCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { CoinCell } from '@/components/ui/coin-cell';
import { AutoAssignDialog } from '@/features/parked-deposits/auto-assign-dialog';
import { applyFilters, type ParkedDepositFilters } from '@/features/parked-deposits/filters';
import { formatParkedTimestamp, formatReasonLabel } from '@/features/parked-deposits/format';
import { IgnoreDialog } from '@/features/parked-deposits/ignore-dialog';
import { ManualAssignDialog } from '@/features/parked-deposits/manual-assign-dialog';
import { ParkedDepositDetailDialog } from '@/features/parked-deposits/parked-deposit-detail-dialog';
import { ParkedDepositFiltersBar } from '@/features/parked-deposits/parked-deposit-filters';
import { PARKED_DEPOSITS_QUERY_KEY, parkedDepositsQueryOptions, useParkedDeposits } from '@/hooks/queries/funds/use-parked-deposits';
import { getErrorMessage } from '@/lib/api/error';
import { type ParkedDeposit, revertParkedDeposit } from '@/services/api/parked-deposits';
import { truncateWithEllipsis } from '@/utils/string';

// Stable fallback — avoids fresh array allocations feeding derived render values
const EMPTY_DEPOSITS: ParkedDeposit[] = [];

const PAGE_SIZE = 50;
const CURRENCY_ICON_SIZE = 32;
const REASON_TRUNCATE_LENGTH = 12;

const ReasonCell = ({ value }: { value: string }) => {
  const label = formatReasonLabel(value);
  return (
    <span className="whitespace-nowrap" title={label}>
      {truncateWithEllipsis(label, REASON_TRUNCATE_LENGTH)}
    </span>
  );
};
ReasonCell.displayName = 'ReasonCell';

const toSortableAmount = (value: unknown): number => {
  const amount = Number(value);
  return Number.isNaN(amount) ? 0 : amount;
};

// Column defs render in a fixed order; a column renders only when at least one record
// contains its field (legacy BopDataTable behaviour). All columns show while the
// list is empty so loading skeletons have headers.
const buildColumns = (deposits: ParkedDeposit[]): ColumnDef<ParkedDeposit, unknown>[] => {
  const hasField = (field: string): boolean => deposits.length === 0 || deposits.some((deposit) => Object.hasOwn(deposit, field));

  const allColumns: Array<{ field: string; column: ColumnDef<ParkedDeposit, unknown> }> = [
    {
      field: 'transaction_id',
      column: {
        accessorKey: 'transaction_id',
        header: 'Tx Id',
        enableSorting: false,
        cell: ({ row }) => <UuidCell value={row.original.transaction_id} />,
      },
    },
    {
      field: 'parked_at',
      column: {
        accessorKey: 'parked_at',
        header: 'Date/Time',
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatParkedTimestamp(row.original.parked_at)}</span>,
      },
    },
    {
      field: 'currency_code',
      column: {
        accessorKey: 'currency_code',
        header: 'Currency',
        enableSorting: false,
        cell: ({ row }) => <CoinCell asset={row.original.currency_code} size={CURRENCY_ICON_SIZE} />,
      },
    },
    {
      field: 'amount',
      column: {
        accessorKey: 'amount',
        header: 'Amount',
        sortingFn: (a, b) => toSortableAmount(a.original.amount) - toSortableAmount(b.original.amount),
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{row.original.amount}</span>,
      },
    },
    {
      field: 'parked_reason',
      column: {
        accessorKey: 'parked_reason',
        header: 'Reason',
        enableSorting: false,
        cell: ({ row }) => <ReasonCell value={row.original.parked_reason} />,
      },
    },
    {
      field: 'reference',
      column: {
        accessorKey: 'reference',
        header: 'Reference',
        enableSorting: false,
      },
    },
    {
      field: 'counterparty_name',
      column: {
        accessorKey: 'counterparty_name',
        header: 'Name',
        enableSorting: false,
      },
    },
    {
      field: 'counterparty_iban',
      column: {
        accessorKey: 'counterparty_iban',
        header: 'IBAN',
        enableSorting: false,
      },
    },
  ];

  return allColumns.filter(({ field }) => hasField(field)).map(({ column }) => column);
};

const ParkedDepositsPage = () => {
  const queryClient = useQueryClient();
  const query = useParkedDeposits();

  const [filters, setFilters] = useState<ParkedDepositFilters>({});
  const [selectedDeposits, setSelectedDeposits] = useState<ParkedDeposit[]>([]);
  // Bumping remounts the DataTable to clear its internal row selection, so stale selected ids can't survive a refetch
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const [detailDeposit, setDetailDeposit] = useState<ParkedDeposit | null>(null);
  const [autoAssignIds, setAutoAssignIds] = useState<string[] | null>(null);
  const [ignoreTargetId, setIgnoreTargetId] = useState<string | null>(null);
  const [manualAssignTargetId, setManualAssignTargetId] = useState<string | null>(null);

  const deposits = query.data ?? EMPTY_DEPOSITS;
  // Deliberate fix: filtering happens BEFORE the table, so the table's
  // select-all only ever selects currently visible rows — legacy selected every
  // loaded deposit even when filter chips hid most of them.
  const filteredDeposits = useMemo(() => applyFilters(query.data ?? EMPTY_DEPOSITS, filters), [query.data, filters]);
  const columns = useMemo(() => buildColumns(query.data ?? EMPTY_DEPOSITS), [query.data]);

  const clearSelection = useCallback(() => {
    setSelectedDeposits([]);
    setSelectionResetKey((key) => key + 1);
  }, []);

  const refreshList = useCallback(() => {
    clearSelection();
    return queryClient.invalidateQueries({ queryKey: PARKED_DEPOSITS_QUERY_KEY });
  }, [clearSelection, queryClient]);

  // Revert opens the audit-justification dialog directly, with the detail
  // dialog still behind it; success closes the detail dialog and refetches.
  const handleRevert = (deposit: ParkedDeposit) => {
    const transactionId = deposit.transaction_id;
    void confirmAction({
      title: 'Confirm Revert Parked Deposit',
      description: `Proceeding with this action will revert the parked deposit ${transactionId}. Your action here will be logged and will be auditable.`,
      actionLabel: 'Revert Parked Deposit',
      onConfirm: async (justification) => {
        try {
          await revertParkedDeposit({ processParkedDepositId: transactionId, comment: justification });
        } catch (error) {
          // Workaround: bop-api's revert handler writes its audit record with
          // an undefined partition key (it reads `transactionId` instead of
          // `processParkedDepositId`), turning an already-successful revert into a 500.
          // Refetch and check whether the deposit disappeared before declaring failure.
          const refreshed = await queryClient.fetchQuery(parkedDepositsQueryOptions());
          if (refreshed.some((item) => item.transaction_id === transactionId)) throw error;
          toast.warning(`Revert succeeded, but bop-api reported an error writing its audit record (known upstream bug): ${getErrorMessage(error)}`);
        }
        toast.success(`Parked deposit ${transactionId} reverted`);
        setDetailDeposit(null);
        await refreshList();
      },
    });
  };

  const isLoading = query.isPending;
  const hasDeposits = deposits.length > 0;

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4">
      <h1 className="text-2xl font-semibold">Parked Deposits</h1>

      {query.isError ? (
        // Deliberate fix: legacy stored a network-error flag and rendered nothing
        <QueryErrorBanner error={query.error} onRetry={() => query.refetch()} title="Failed to load parked deposits" />
      ) : (
        <>
          {/* Controls row. Deviation: the "Select All" checkbox lives in the
              table's header selection column (DataTable owns its selection state), so
              the row carries the count + action button only. */}
          {hasDeposits && !isLoading ? (
            <div className="flex items-center justify-end gap-3">
              <span className="text-sm text-muted-foreground">{selectedDeposits.length} selected</span>
              <Button
                className="gap-2"
                disabled={selectedDeposits.length === 0}
                onClick={() => setAutoAssignIds(selectedDeposits.map((deposit) => deposit.transaction_id))}
                type="button"
              >
                <WandSparkles className="size-4" />
                Auto Assign
              </Button>
            </div>
          ) : null}

          {/* Filter chips row directly above the table (purely client-side) */}
          {hasDeposits && !isLoading ? <ParkedDepositFiltersBar deposits={deposits} filters={filters} onFiltersChange={setFilters} /> : null}

          <DataTable
            columns={columns}
            data={filteredDeposits}
            emptyMessage="No results"
            enableRowSelection
            getRowId={(deposit) => deposit.transaction_id}
            isLoading={isLoading}
            key={selectionResetKey}
            onRowClick={setDetailDeposit}
            onSelectionChange={setSelectedDeposits}
            pageSize={PAGE_SIZE}
          />
        </>
      )}

      {detailDeposit ? (
        <ParkedDepositDetailDialog
          deposit={detailDeposit}
          onAutoAssign={(deposit) => {
            setDetailDeposit(null);
            setAutoAssignIds([deposit.transaction_id]);
          }}
          onClose={() => setDetailDeposit(null)}
          onIgnore={(deposit) => {
            setDetailDeposit(null);
            setIgnoreTargetId(deposit.transaction_id);
          }}
          onManualAssign={(deposit) => {
            setDetailDeposit(null);
            setManualAssignTargetId(deposit.transaction_id);
          }}
          onRevert={handleRevert}
        />
      ) : null}

      {autoAssignIds ? (
        <AutoAssignDialog
          onClose={(didRun) => {
            setAutoAssignIds(null);
            if (didRun) void refreshList();
          }}
          onRunCompleted={clearSelection}
          transactionIds={autoAssignIds}
        />
      ) : null}

      {ignoreTargetId ? (
        <IgnoreDialog
          onClose={() => setIgnoreTargetId(null)}
          onCompleted={() => {
            setIgnoreTargetId(null);
            void refreshList();
          }}
          transactionId={ignoreTargetId}
        />
      ) : null}

      {manualAssignTargetId ? (
        <ManualAssignDialog
          onClose={() => setManualAssignTargetId(null)}
          onCompleted={() => {
            setManualAssignTargetId(null);
            void refreshList();
          }}
          transactionId={manualAssignTargetId}
        />
      ) : null}
    </div>
  );
};
ParkedDepositsPage.displayName = 'ParkedDepositsPage';

export default ParkedDepositsPage;
